import axios from '../axiosConfig'
import { getEnv } from '@utils/env'
import type { DashboardPeriod } from './finance.api'

const BUSINESS_LAYER_API = getEnv('REACT_APP_BUSINESS_LAYER_DASHBOARD')

interface ApiResponse<T> {
  statusCode: number
  message: string
  data: T
}

export interface ClinicalEfficiencyQueryParams {
  clinicId: string
  date: string
  period: DashboardPeriod
}

export interface ClinicalEfficiencyKpiData {
  radiographCompliance: string
  avgTimePerPatient: string
  avgProviderUtilization: string
  treatmentOpportunities: string
  radiographGoal: string
  avgTimeGoal: string
  utilizationGoal: string
  treatmentOpportunitiesGoal: string
}

export interface ClinicalEfficiencyChartPoint {
  label: string
  avgMins: number
  isCurrent?: boolean
}

export interface ClinicalEfficiencyTimeChartPoint {
  label: string
  hours: number
  isCurrent?: boolean
}

export interface ClinicalEfficiencyChartConfig {
  data: ClinicalEfficiencyChartPoint[]
  yDomain: [number, number]
  yTicks: number[]
}

export interface ClinicalEfficiencyTimeChartConfig {
  data: ClinicalEfficiencyTimeChartPoint[]
  yDomain: [number, number]
  yTicks: number[]
}

export interface ClinicalEfficiencyProviderRow {
  id: string
  provider: string
  radiographMins: string
  avgTimePatient: string
  utilization: string
  txOpportunities: string
}

export interface ClinicalEfficiencyOverviewResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  goals: null
  kpis: ClinicalEfficiencyKpiData
}

export interface ClinicalEfficiencyDetailResult {
  period: DashboardPeriod
  periodStart: string
  periodEnd: string
  kpis: ClinicalEfficiencyKpiData
  radiographChart: ClinicalEfficiencyChartConfig
  avgTimeChart: ClinicalEfficiencyTimeChartConfig
  byProvider: ClinicalEfficiencyProviderRow[]
}

const clinicalEfficiencyGet = <T>(
  path: string,
  params: ClinicalEfficiencyQueryParams,
  options?: { signal?: AbortSignal },
): Promise<T> =>
  axios
    .get<
      ApiResponse<T>
    >(`${BUSINESS_LAYER_API}/api/v1/dashboard/scheduling/clinical-efficiency/${path}`, { params, signal: options?.signal })
    .then((response) => response.data.data)

export const getClinicalEfficiencyOverview = (
  params: ClinicalEfficiencyQueryParams,
  options?: { signal?: AbortSignal },
): Promise<ClinicalEfficiencyOverviewResult> =>
  clinicalEfficiencyGet('overview', params, options)

export const getClinicalEfficiencyDetail = (
  params: ClinicalEfficiencyQueryParams,
  options?: { signal?: AbortSignal },
): Promise<ClinicalEfficiencyDetailResult> =>
  clinicalEfficiencyGet('detail', params, options)
